import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Button,
  cn,
  PaginatedList,
  useDateFnsFormattersWithLocale,
  Badge,
  SearchInput,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { Upload, FileIcon } from "lucide-react";
import { formatFileSize } from "@tellescope/utils";
import { ExistingFileImagePreview } from "../ExistingFileImagePreview";
import type { useFilesVariant } from "./useFilesVariant";

export interface FilesVariantContentProps {
  logic: ReturnType<typeof useFilesVariant>;
}

export const FilesVariantContent = ({ logic }: FilesVariantContentProps) => {
  const { t } = useTranslation("inbox");
  const { format } = useDateFnsFormattersWithLocale();
  const {
    activeTab,
    changeTab,
    selectedFiles,
    selectedExistingFiles,
    fileSearch,
    setFileSearch,
    debouncedFileSearch,
    fileInputRef,
    existingFilesQuery,
    maxFileSizeMB,
    acceptedFileTypes,
    channelType,
    handleFileChange,
    handleRemoveFile,
    toggleExistingFile,
    clearExistingSelection,
  } = logic;

  return (
    <Tabs
      value={activeTab}
      onValueChange={(val) => changeTab(val === "existing" ? "existing" : "upload")}
      className="flex-1 flex flex-col overflow-hidden"
    >
      <TabsList className="grid w-auto grid-cols-2 mx-4">
        <TabsTrigger value="upload">{t("Upload New")}</TabsTrigger>
        <TabsTrigger value="existing">{t("Existing Files")}</TabsTrigger>
      </TabsList>
      <TabsContent value="upload" className="flex-1 overflow-y-auto space-y-4 p-4 border-t">
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={acceptedFileTypes}
            onChange={handleFileChange}
            className="hidden"
          />
          <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-sm text-muted-foreground mb-2">
            {channelType === "sms" ? t("Drop images here or click to select") : t("Drop files here or click to select")}
          </p>
          <p className="text-xs text-muted-foreground mb-4">
            {t("Maximum file size")}: {maxFileSizeMB}MB
            {channelType === "sms" && ` (${t("Images only")})`}
          </p>
          <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
            {t("Select Files")}
          </Button>
        </div>

        {selectedFiles.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">
              {t("Selected files")} ({selectedFiles.length})
            </p>
            {selectedFiles.map((file, index) => (
              <div key={`${file.name}-${index}`} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <FileIcon className="h-4 w-4 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{file.name}</p>
                    <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveFile(index)}>
                  {t("Remove")}
                </Button>
              </div>
            ))}
          </div>
        )}
      </TabsContent>
      <TabsContent value="existing" className="flex-1 flex flex-col overflow-hidden py-4 border-t">
        <div className="px-4 space-y-2">
          <SearchInput
            placeholder={t("Search files...")}
            value={fileSearch}
            onChange={(e) => setFileSearch(e.target.value)}
          />
          <span className="flex items-center text-xs text-muted-foreground justify-between">
            {t("{{count}} items selected", { count: selectedExistingFiles.size })}
            <Button
              variant="ghost"
              size="sm"
              className="text-xs font-light"
              onClick={clearExistingSelection}
              disabled={!selectedExistingFiles.size}
            >
              {t("Clear")}
            </Button>
          </span>
        </div>
        {existingFilesQuery.isLoading || fileSearch !== debouncedFileSearch ? (
          <div className="flex items-center justify-center h-32">
            <p className="text-sm text-muted-foreground">{t("Loading files...")}</p>
          </div>
        ) : existingFilesQuery.isError ? (
          <div className="flex items-center justify-center h-32">
            <p className="text-sm text-destructive">{t("Error loading files")}</p>
          </div>
        ) : !existingFilesQuery.data?.length ? (
          <div className="flex items-center justify-center h-32">
            <p className="text-sm text-muted-foreground">{t("No files found")}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 flex-1 min-h-0">
            <PaginatedList className="flex-1 min-h-0 px-4" onBottomReached={() => existingFilesQuery.fetchNextPage()}>
              {existingFilesQuery.data?.map((file) => (
                <button
                  key={file.id}
                  className={cn(
                    "flex items-center gap-3 py-3 px-4 hover:bg-gray-50 cursor-pointer w-full justify-start text-left border rounded-md transition-all mb-2",
                    selectedExistingFiles.has(file.secureName) && "border border-primary bg-gray-100"
                  )}
                  onClick={() => toggleExistingFile(file.secureName)}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0 justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {t("Added {{date}}", { date: format(file.createdAt, "MMMM d, yyyy") })} •{" "}
                        {formatFileSize(file.size)}
                      </p>
                      <span className="flex gap-2 items-center mt-2">
                        {file.tags?.map((tag) => (
                          <Badge key={`${tag}-${file.secureName}`} variant="secondary">
                            {tag}
                          </Badge>
                        ))}
                      </span>
                    </div>
                    <ExistingFileImagePreview secureName={file.secureName} type={file.type} name={file.name} />
                  </div>
                </button>
              ))}
            </PaginatedList>
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
};
